import type { CfgFile, Config, ConfigMeta, ConfigSummary, ConfigVersionInput, CreateConfigInput, Scope } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.ts";

export const configKeys = {
  all: ["configs"] as const,
  list: (scope: Scope) => ["configs", "list", scope] as const,
  one: (id: string) => ["configs", "one", id] as const,
  version: (id: string, v: number) => ["configs", "version", id, v] as const,
};

export const useConfigs = (scope: Scope) =>
  useQuery({ queryKey: configKeys.list(scope), queryFn: () => api<ConfigSummary[]>("GET", `/configs?scope=${encodeURIComponent(scope)}`) });

export const useConfig = (id: string | undefined) =>
  useQuery({ queryKey: configKeys.one(id ?? ""), queryFn: () => api<Config>("GET", `/configs/${id}`), enabled: !!id });

/** An older text, for the version picker and "what changed". */
export const useConfigVersion = (id: string, version: number | null) =>
  useQuery({
    queryKey: configKeys.version(id, version ?? 0),
    queryFn: () => api<{ version: number; text: string; files: CfgFile[] }>("GET", `/configs/${id}/versions/${version}`),
    enabled: version !== null && version > 0,
    staleTime: Infinity,
  });

export function useCreateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConfigInput) => api<Config>("POST", "/configs", input),
    onSuccess: (c) => {
      qc.setQueryData(configKeys.one(c.id), c);
      return qc.invalidateQueries({ queryKey: configKeys.all });
    },
  });
}

export function useUpdateConfig(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meta: ConfigMeta) => api<Config>("PUT", `/configs/${id}`, meta),
    onSuccess: (c) => {
      qc.setQueryData(configKeys.one(c.id), c);
      return qc.invalidateQueries({ queryKey: configKeys.all });
    },
  });
}

export function useNewConfigVersion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pack: ConfigVersionInput) => api<Config>("POST", `/configs/${id}/versions`, pack),
    onSuccess: (c) => {
      qc.setQueryData(configKeys.one(c.id), c);
      return qc.invalidateQueries({ queryKey: configKeys.all });
    },
  });
}

export function useDeleteConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/configs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: configKeys.all }),
  });
}

/** Reads a chosen .cfg file as text (configs are small). */
export function readTextFile(file: File): Promise<string> {
  return file.text();
}
